import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DataSourceBadge } from "@/components/DataSourceBadge";
import { Loader2, FileText, Download, Copy, Check, Sparkles, ExternalLink, ShieldCheck, PencilLine } from "lucide-react";
import { toast } from "sonner";

/**
 * Sub-component: predicted AEO score if the writer follows the brief exactly.
 * Meant to motivate compliance — big score, clear "you already have this" checklist.
 */
function ScorePreview({ preview }) {
    const min = preview.predicted_min;
    const max = preview.predicted_max;
    const pctMin = Math.round((min / 100) * 100);
    return (
        <div className="border border-primary/40 bg-primary/5 p-5" data-testid="score-preview">
            <div className="flex items-start gap-5 flex-wrap">
                {/* Circular-ish gauge */}
                <div className="flex-1 min-w-[180px]">
                    <div className="overline text-primary flex items-center gap-2 mb-2">
                        <ShieldCheck className="w-3 h-3" /> AEO SCORE PREVIEW
                    </div>
                    <div className="text-xs text-muted-foreground mb-3">
                        Follow the brief and this piece <span className="text-foreground">already ships</span> with:
                    </div>
                    <div className="flex items-baseline gap-2">
                        <div className="font-mono text-5xl text-primary" data-testid="predicted-score">{min}</div>
                        <div className="text-sm text-muted-foreground">/ 100 guaranteed</div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                        Room to hit <span className="text-success">{max}/100</span> if you handle keyword density + add real stats.
                    </div>
                    {/* Progress bar */}
                    <div className="mt-4 h-2 bg-background border border-border relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-primary" style={{ width: `${pctMin}%` }} />
                        <div className="absolute inset-y-0 border-r border-dashed border-success" style={{ left: `${max}%` }} title={`Max ${max}`} />
                    </div>
                </div>
            </div>

            {/* Checks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div>
                    <div className="text-[10px] text-success uppercase tracking-widest font-mono mb-2 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Guaranteed by brief
                    </div>
                    <ul className="space-y-1.5">
                        {preview.checks_guaranteed.map((c) => (
                            <li key={c.label} className="text-xs flex items-start gap-2">
                                <Check className="w-3.5 h-3.5 text-success shrink-0 mt-0.5" />
                                <span className="flex-1">{c.label}</span>
                                <span className="font-mono text-muted-foreground">+{c.weight}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <div className="text-[10px] text-warning uppercase tracking-widest font-mono mb-2 flex items-center gap-1">
                        <PencilLine className="w-3 h-3" /> Your job (writer effort)
                    </div>
                    <ul className="space-y-1.5">
                        {preview.checks_writer_effort.map((c) => (
                            <li key={c.label} className="text-xs">
                                <div className="flex items-start gap-2">
                                    <PencilLine className="w-3.5 h-3.5 text-warning shrink-0 mt-0.5" />
                                    <span className="flex-1">{c.label}</span>
                                    <span className="font-mono text-muted-foreground">+{c.weight}</span>
                                </div>
                                {c.note && <div className="text-[11px] text-muted-foreground ml-5 mt-0.5">{c.note}</div>}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}


/**
 * Content Brief Generator - takes a target keyword, pulls the live top-N SERP,
 * and asks Claude to produce a full editor-ready brief seeded with what
 * actually ranks.
 */
export function ContentBrief({ open, onOpenChange, projectId, initialKeyword = "" }) {
    const [keyword, setKeyword] = useState(initialKeyword);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const [copied, setCopied] = useState(false);

    // Auto-run if we open with a keyword pre-filled
    useEffect(() => {
        if (open && initialKeyword && initialKeyword !== keyword) {
            setKeyword(initialKeyword);
        }
        // eslint-disable-next-line
    }, [open, initialKeyword]);

    useEffect(() => {
        if (open && initialKeyword) {
            run(initialKeyword);
        }
        // eslint-disable-next-line
    }, [open, initialKeyword]);

    const run = async (kw) => {
        const target = (kw ?? keyword ?? "").trim();
        if (!target) return;
        setData(null);
        setErr(null);
        setLoading(true);
        try {
            const r = await client.post(`/content/brief`, { project_id: projectId, keyword: target });
            setData(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Could not build the brief");
        } finally {
            setLoading(false);
        }
    };

    const copyBrief = async () => {
        if (!data?.brief) return;
        try {
            await navigator.clipboard.writeText(data.brief);
            setCopied(true);
            toast.success("Brief copied to clipboard");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Copy failed");
        }
    };

    const download = () => {
        if (!data?.brief) return;
        const md = `# Content Brief · ${data.keyword}\n\n_${new Date(data.generated_at).toLocaleString()} · volume ${data.volume?.toLocaleString?.() || "?"}/mo · CPC $${data.cpc}_\n\n${data.brief}`;
        const blob = new Blob([md], { type: "text/markdown" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `brief-${data.keyword.replace(/[^a-z0-9-]+/gi, "-").toLowerCase()}-${new Date().toISOString().slice(0, 10)}.md`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                data-testid="content-brief-sheet"
                side="right"
                className="w-full sm:max-w-2xl overflow-y-auto bg-background border-l border-border"
            >
                <SheetHeader>
                    <div className="overline flex items-center gap-2">
                        <FileText className="w-3 h-3" /> AI CONTENT BRIEF
                        {data && <DataSourceBadge source={data.data_source === "live" ? "dataforseo" : "mock"} />}
                    </div>
                    <SheetTitle className="font-display text-2xl tracking-tight">
                        Brief seeded with what actually ranks.
                    </SheetTitle>
                    <SheetDescription className="text-muted-foreground text-sm">
                        Claude reads the live top-10 SERP and produces an editor-ready outline: intent, format, headings, entities, and opening paragraph.
                    </SheetDescription>
                </SheetHeader>

                {/* Keyword input */}
                <form
                    onSubmit={(e) => { e.preventDefault(); run(); }}
                    className="mt-6 flex gap-2"
                >
                    <input
                        data-testid="brief-keyword-input"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        placeholder="Target keyword (e.g. best crm software)"
                        className="bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary flex-1"
                    />
                    <button
                        data-testid="brief-run-btn"
                        type="submit"
                        disabled={loading || !keyword.trim()}
                        className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60"
                        title="Generating a brief costs 5 credits"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {loading ? "Drafting…" : "Generate"}
                        <span className="opacity-70 text-[10px] font-mono">· 5 credits</span>
                    </button>
                </form>

                {loading && (
                    <div className="mt-10 flex flex-col items-center justify-center gap-3 py-16" data-testid="brief-loading">
                        <Loader2 className="w-6 h-6 text-primary animate-spin" />
                        <div className="text-sm text-muted-foreground font-mono">Reading the live SERP and drafting the brief…</div>
                        <div className="text-xs text-muted-foreground">~15-25 seconds</div>
                    </div>
                )}

                {err && (
                    <div className="mt-6 text-sm text-destructive border border-destructive/40 bg-destructive/5 p-4" data-testid="brief-error">
                        {err}
                    </div>
                )}

                {data && !loading && (
                    <div className="mt-6 space-y-6" data-testid="brief-content">
                        {/* Metrics */}
                        <div className="grid grid-cols-3 gap-3">
                            <div className="border border-border p-4">
                                <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Volume</div>
                                <div className="font-mono text-xl mt-1">{data.volume?.toLocaleString?.() || "—"}<span className="text-xs text-muted-foreground">/mo</span></div>
                            </div>
                            <div className="border border-border p-4">
                                <div className="text-[10px] text-muted-foreground uppercase tracking-widest">CPC</div>
                                <div className="font-mono text-xl mt-1">${data.cpc}</div>
                            </div>
                            <div className="border border-border p-4">
                                <div className="text-[10px] text-muted-foreground uppercase tracking-widest">AI Overview</div>
                                <div className="font-mono text-xl mt-1 flex items-center gap-1">
                                    {data.ai_overview ? <><Sparkles className="w-4 h-4 text-secondary" /> Yes</> : "No"}
                                </div>
                            </div>
                        </div>

                        {/* Top competitors */}
                        {data.top_competitors?.length > 0 && (
                            <div>
                                <div className="overline mb-2">Top {data.top_competitors.length} results (source material)</div>
                                <div className="border border-border divide-y divide-border max-h-64 overflow-y-auto">
                                    {data.top_competitors.map((c, i) => (
                                        <div key={i} className="p-3 flex items-start gap-3 text-sm">
                                            <div className="font-mono text-xs text-muted-foreground w-6 pt-0.5">#{c.position}</div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-mono text-xs text-primary truncate">{c.domain}</div>
                                                <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{c.title}</div>
                                            </div>
                                            {c.url && (
                                                <a href={c.url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary shrink-0">
                                                    <ExternalLink className="w-3.5 h-3.5" />
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Score Preview */}
                        {data.score_preview && <ScorePreview preview={data.score_preview} />}

                        {/* Brief */}
                        {data.brief && (
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="overline">The brief</div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={copyBrief} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1" data-testid="brief-copy-btn">
                                            {copied ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
                                            {copied ? "Copied" : "Copy"}
                                        </button>
                                        <button onClick={download} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="brief-download-btn">
                                            <Download className="w-3 h-3" /> Download .md
                                        </button>
                                    </div>
                                </div>
                                <pre data-testid="brief-output"
                                     className="whitespace-pre-wrap font-sans text-sm text-foreground bg-card/40 border border-border p-5 leading-relaxed">
{data.brief}
                                </pre>
                            </div>
                        )}

                        {!data.brief && (
                            <div className="text-sm text-muted-foreground border border-border p-4">
                                AI brief generation temporarily unavailable. {data.ai_error && <span className="text-destructive/80 block mt-2 font-mono text-xs">{data.ai_error}</span>}
                            </div>
                        )}
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
